/**
 * SearchAutocomplete — typeahead dropdown that suggests both curated rails
 * and TMDB titles as the user types. Debounced 150ms (v3.50 reduced from 220ms).
 * Up to 8 suggestions.
 *
 * Performance layers (v3.50):
 *   1. INSTANT  — Local RAIL_KEYWORD_MAP: genre/mood keywords resolved in <1ms,
 *                 no network call. Rail pills appear before the user stops typing.
 *   2. FAST     — In-memory + localStorage SWR cache (5-min TTL) so repeated
 *                 queries (same session or next visit) paint instantly.
 *   3. DEFERRED — Backend /api/search/suggest fires (150ms debounce) for
 *                 title/person/actor/director results that need TMDB lookup.
 *                 Results are merged with the already-visible local rails.
 *
 * Uses /api/search/suggest under the hood. Each suggestion has:
 *   { type: "rail" | "title" | "person", label, sublabel, image?, href }
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Sparkles, User, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { readSwrCache, writeSwrCache } from "../lib/swrCache";

const DEBOUNCE_MS = 150;   // v3.50 reduced from 220ms
const MIN_CHARS = 2;
const SUGGEST_CACHE_TTL = 5 * 60 * 1000;   // 5 min in-memory
const SUGGEST_SWR_TTL  = 10 * 60 * 60 * 1000; // 10 h localStorage

// ─── Module-level in-memory cache (lives for the tab session) ─────────────
// Map<lowercase_query → { ts: number, data: suggestion[] }>
const _SUGGEST_MEM = new Map();

// ─── Local rail keyword map (INSTANT, zero network) ────────────────────────
// Mirrors backend utils/search_intelligence.py GENRE_TOKEN_MAP.
// Slug → { label, href } and keyword → [slug, ...]
const RAIL_META = {
  "romantic-films":           { label: "Romantic Cinema — Love Stories",    href: "/collection/romantic-films" },
  "horror-hits":              { label: "Horror Hits",                        href: "/collection/horror-hits" },
  "indian-folk-horror":       { label: "Indian Folk Horror & Supernatural",  href: "/collection/indian-folk-horror" },
  "fantasy-worlds":           { label: "Fantasy Worlds",                     href: "/collection/fantasy-worlds" },
  "mind-bending-thrillers":   { label: "Mind-Bending Thrillers",             href: "/collection/mind-bending-thrillers" },
  "best-plot-twists":         { label: "Best Plot Twists",                   href: "/collection/best-plot-twists" },
  "intelligent-scifi":        { label: "Intelligent Sci-Fi",                 href: "/collection/intelligent-scifi" },
  "high-octane-action":       { label: "High-Octane Action",                 href: "/collection/high-octane-action" },
  "comedy-hits":              { label: "Comedy Hits",                        href: "/collection/comedy-hits" },
  "stand-up-comedy":          { label: "Stand-Up Comedy",                    href: "/collection/stand-up-comedy" },
  "all-documentaries":        { label: "Documentaries",                      href: "/collection/all-documentaries" },
  "nature-documentaries":     { label: "Nature & Wildlife Documentaries",    href: "/collection/nature-documentaries" },
  "sports-docs":              { label: "Sports Documentaries",               href: "/collection/sports-docs" },
  "music-docs":               { label: "Music & Concert Documentaries",      href: "/collection/music-docs" },
  "animation":                { label: "Animation",                          href: "/collection/animation" },
  "cartoon-network-classics": { label: "Cartoon Network Fans",               href: "/collection/cartoon-network-classics" },
  "k-drama-obsession":        { label: "K-Drama Obsession",                  href: "/collection/k-drama-obsession" },
  "marvel-magic":             { label: "Marvel Magic",                       href: "/collection/marvel-magic" },
  "ghibli-magic":             { label: "For Ghibli Lovers",                  href: "/collection/ghibli-magic" },
  "family-favourites":        { label: "Family Favourites",                  href: "/collection/family-favourites" },
  "motivational-films":       { label: "Motivational & Inspirational",       href: "/collection/motivational-films" },
  "feel-good":                { label: "Feel-Good Cinema",                   href: "/collection/feel-good" },
  "survival-thrillers":       { label: "Survival & Disaster Thrillers",      href: "/collection/survival-thrillers" },
  "corporate-startup-stories":{ label: "Corporate & Startup Stories",        href: "/collection/corporate-startup-stories" },
  "all-time-hit-series":      { label: "All-Time Hit Series",                href: "/collection/all-time-hit-series" },
  "weekend-binge-picks":      { label: "Weekend Binge Picks",                href: "/collection/weekend-binge-picks" },
  "emotional-masterpieces":   { label: "Emotional Masterpieces",             href: "/collection/emotional-masterpieces" },
};

const RAIL_KEYWORD_MAP = {
  // Romance
  romance: ["romantic-films"], romantic: ["romantic-films"], love: ["romantic-films"],
  romcom: ["romantic-films"],
  // Horror
  horror: ["horror-hits"], scary: ["horror-hits"], ghost: ["horror-hits"],
  haunted: ["horror-hits"], supernatural: ["indian-folk-horror","horror-hits"],
  folk: ["indian-folk-horror"], bhoot: ["indian-folk-horror"],
  // Fantasy
  fantasy: ["fantasy-worlds"], magic: ["fantasy-worlds"], magical: ["fantasy-worlds"],
  wizards: ["fantasy-worlds"],
  // Thriller / Mystery
  thriller: ["mind-bending-thrillers"], thrillers: ["mind-bending-thrillers"],
  twist: ["best-plot-twists","mind-bending-thrillers"], twists: ["best-plot-twists"],
  mystery: ["mind-bending-thrillers"], detective: ["mind-bending-thrillers"],
  psychological: ["mind-bending-thrillers"], whodunit: ["mind-bending-thrillers"],
  crime: ["mind-bending-thrillers"],
  // Sci-Fi
  scifi: ["intelligent-scifi"], "sci-fi": ["intelligent-scifi"], space: ["intelligent-scifi"],
  dystopia: ["intelligent-scifi"], dystopian: ["intelligent-scifi"], robots: ["intelligent-scifi"],
  // Action
  action: ["high-octane-action"], fight: ["high-octane-action"], martial: ["high-octane-action"],
  war: ["high-octane-action"], combat: ["high-octane-action"],
  // Comedy
  comedy: ["comedy-hits"], comedies: ["comedy-hits"], funny: ["comedy-hits"],
  // Standup
  standup: ["stand-up-comedy"], "stand-up": ["stand-up-comedy"],
  // Documentary
  documentary: ["all-documentaries"], documentaries: ["all-documentaries"],
  docs: ["all-documentaries"], docu: ["all-documentaries"],
  wildlife: ["nature-documentaries"], nature: ["nature-documentaries"],
  sports: ["sports-docs"], music: ["music-docs"],
  // Animation
  animation: ["animation"], animated: ["animation"], anime: ["animation"],
  cartoon: ["cartoon-network-classics","animation"], cartoons: ["cartoon-network-classics"],
  // K-Drama
  kdrama: ["k-drama-obsession"], korean: ["k-drama-obsession"], kdramas: ["k-drama-obsession"],
  // Marvel / Superhero
  marvel: ["marvel-magic"], mcu: ["marvel-magic"], superhero: ["marvel-magic"],
  superheroes: ["marvel-magic"], avengers: ["marvel-magic"],
  // Ghibli
  ghibli: ["ghibli-magic"], miyazaki: ["ghibli-magic"],
  // Family
  family: ["family-favourites"], kids: ["family-favourites","animation"],
  children: ["family-favourites"],
  // Motivation
  motivational: ["motivational-films"], inspirational: ["motivational-films"],
  motivation: ["motivational-films"], inspiring: ["motivational-films"],
  // Feel-good
  feelgood: ["feel-good"], happy: ["feel-good"], uplifting: ["feel-good"],
  wholesome: ["feel-good"],
  // Survival
  survival: ["survival-thrillers"], disaster: ["survival-thrillers"],
  // Series
  series: ["all-time-hit-series"], shows: ["all-time-hit-series"], webseries: ["all-time-hit-series"],
  binge: ["weekend-binge-picks"],
  // Emotional
  emotional: ["emotional-masterpieces"], sad: ["emotional-masterpieces"],
  touching: ["emotional-masterpieces"],
};

/**
 * Instantly resolve rail suggestions from local keyword map.
 * Returns up to 3 rail pills in <1ms with zero network.
 */
function getLocalRailSuggestions(qs) {
  if (qs.length < 3) return [];
  const seen = new Set();
  const results = [];
  // Word-by-word matching (handles "thriller movies", "scary indian", etc.)
  const words = qs.split(/\s+/);
  for (const word of words) {
    // Exact token match first
    const slugs = RAIL_KEYWORD_MAP[word] || [];
    for (const slug of slugs) {
      if (seen.has(slug) || !RAIL_META[slug]) continue;
      seen.add(slug);
      results.push({ type: "rail", slug, ...RAIL_META[slug], sublabel: "Curated collection" });
      if (results.length >= 3) return results;
    }
    // Prefix match (e.g. "acti" → "action")
    for (const [tok, slugs2] of Object.entries(RAIL_KEYWORD_MAP)) {
      if (!tok.startsWith(word) || tok === word) continue;
      for (const slug of slugs2) {
        if (seen.has(slug) || !RAIL_META[slug]) continue;
        seen.add(slug);
        results.push({ type: "rail", slug, ...RAIL_META[slug], sublabel: "Curated collection" });
        if (results.length >= 3) return results;
      }
    }
  }
  return results;
}

export default function SearchAutocomplete({
  placeholder = "Search movies, series, actors…",
  testid = "nav-search-input",
  compact = false,
  variant = "navbar",   // "navbar" (small) | "hero" (big, red CTA)
  onSelect,
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Debounced fetch with 3-tier performance strategy
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const qs = q.trim();
    if (qs.length < MIN_CHARS) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    const qsLower = qs.toLowerCase();

    // ── Tier 1: INSTANT local rail suggestions (0ms, no network) ──────────
    const localRails = getLocalRailSuggestions(qsLower);

    // ── Tier 2: In-memory cache hit → paint immediately ────────────────────
    const memHit = _SUGGEST_MEM.get(qsLower);
    if (memHit && Date.now() - memHit.ts < SUGGEST_CACHE_TTL) {
      setSuggestions(memHit.data);
      setLoading(false);
      return;
    }

    // ── Tier 2b: localStorage SWR cache ────────────────────────────────────
    const swrHit = readSwrCache(`suggest:${qsLower}`, SUGGEST_SWR_TTL);
    if (swrHit?.length) {
      setSuggestions(swrHit);
      _SUGGEST_MEM.set(qsLower, { ts: Date.now(), data: swrHit });
      setLoading(false);
      return;
    }

    // ── Tier 3: Show local rails IMMEDIATELY while API loads ───────────────
    if (localRails.length > 0) {
      setSuggestions(localRails);
    }

    // ── Tier 3: Deferred network call for title/person/actor matches ───────
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const r = await api.get("/search/suggest", {
          params: { q: qs },
          signal: controller.signal,
        });
        const apiSuggestions = r.data?.suggestions || [];

        // Merge: deduplicate rail suggestions, keep title/person from API
        const seen = new Set(localRails.map((s) => s.slug || s.label));
        const merged = [...localRails];
        for (const s of apiSuggestions) {
          const key = s.slug || s.tmdb_id || s.label;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(s);
          }
        }
        const final = merged.slice(0, 8);

        setSuggestions(final);
        setActiveIdx(-1);
        // Write to both caches
        _SUGGEST_MEM.set(qsLower, { ts: Date.now(), data: final });
        writeSwrCache(`suggest:${qsLower}`, final);
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          // Keep local rails visible on API error
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Outside click closes dropdown
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setOpen(false);
    onSelect?.();
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const pickSuggestion = (s) => {
    setOpen(false);
    setQ("");
    setSuggestions([]);
    onSelect?.();
    navigate(s.href);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter") submit(e);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        pickSuggestion(suggestions[activeIdx]);
      } else {
        submit(e);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const isHero = variant === "hero";

  return (
    <form
      onSubmit={submit}
      ref={wrapRef}
      className={`relative ${compact ? "w-full" : isHero ? "w-full" : "ml-auto flex-1 max-w-xs"}`}
      data-testid={isHero ? "hero-search-form" : "nav-search-form"}
    >
      <div className="relative">
        {isHero ? (
          <>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              data-testid={testid}
              className="w-full bg-black/55 backdrop-blur-md border border-white/15 rounded-md pl-11 pr-32 py-4 text-base text-white placeholder:text-neutral-400 focus:outline-none focus:border-[#E50914] focus:shadow-[0_0_0_3px_rgba(229,9,20,0.18)] transition-all"
              autoComplete="off"
            />
            <button
              type="submit"
              data-testid="hero-ask-btn"
              className="absolute right-2 top-1/2 -translate-y-1/2 fm-btn-red px-5 py-2.5 rounded-md text-sm font-semibold flex items-center gap-2"
            >
              Go <ArrowRight className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              data-testid={testid}
              className="w-full bg-white/5 border border-white/10 rounded-md pl-9 pr-9 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-white/30 transition-colors"
              autoComplete="off"
            />
            {q && (
              <button
                type="button"
                onClick={() => { setQ(""); setSuggestions([]); setOpen(false); }}
                data-testid="nav-search-clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-white rounded transition"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </>
        )}
      </div>

      {open && q.trim().length >= MIN_CHARS && (suggestions.length > 0 || loading) && (
        <div
          data-testid={isHero ? "hero-search-dropdown" : "nav-search-dropdown"}
          className={`absolute left-0 right-0 mt-2 ${isHero ? "w-full max-h-[440px]" : "w-full sm:w-[420px] max-h-[420px]"} overflow-y-auto bg-[#101012] border border-white/10 rounded-xl shadow-2xl shadow-black/60 ${isHero ? "z-[9999]" : "z-50"} fm-fade text-left`}
        >
          {/* Blank "Searching..." only when no results at all yet */}
          {loading && suggestions.length === 0 && (
            <div className="px-4 py-3 text-xs text-neutral-500 flex items-center gap-2">
              <span className="inline-block w-3 h-3 border border-neutral-600 border-t-neutral-300 rounded-full animate-spin" />
              Searching…
            </div>
          )}
          {/* Subtle "loading more" bar when we already have local results */}
          {loading && suggestions.length > 0 && (
            <div className="h-[2px] bg-gradient-to-r from-transparent via-[#E50914]/60 to-transparent animate-pulse" />
          )}
          {suggestions.map((s, i) => (
            <button
              key={`${s.type}-${s.tmdb_id || s.slug}-${i}`}
              type="button"
              onClick={() => pickSuggestion(s)}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`nav-search-suggest-${i}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition border-l-2 ${
                activeIdx === i
                  ? "bg-white/[0.06] border-emerald-400"
                  : "border-transparent hover:bg-white/[0.04]"
              }`}
            >
              {s.image ? (
                <img
                  src={s.image}
                  alt=""
                  className={`shrink-0 ${s.type === "person" ? "w-9 h-9 rounded-full" : "w-9 h-12 rounded"} object-cover bg-white/5`}
                  loading="lazy"
                />
              ) : (
                <div className={`shrink-0 flex items-center justify-center ${s.type === "person" ? "w-9 h-9 rounded-full" : "w-9 h-12 rounded"} bg-white/5 text-neutral-500`}>
                  {s.type === "rail" ? <Sparkles className="w-4 h-4 text-emerald-400" /> :
                   s.type === "person" ? <User className="w-4 h-4" /> :
                   <Search className="w-3.5 h-3.5" />}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white font-medium truncate">{s.label}</p>
                <p className="text-[11px] text-neutral-500 truncate">{s.sublabel}</p>
              </div>
              {s.type === "rail" && (
                <span className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold shrink-0">Rail</span>
              )}
            </button>
          ))}
          {suggestions.length > 0 && (
            <button
              type="button"
              onClick={submit}
              data-testid="nav-search-see-all"
              className="w-full px-4 py-2.5 text-xs text-neutral-400 hover:text-white hover:bg-white/[0.04] border-t border-white/5 text-left transition"
            >
              See all results for "<span className="text-white">{q}</span>" →
            </button>
          )}
        </div>
      )}
    </form>
  );
}
